/**
 * SyncStatusBadge - Badge compatto per mostrare lo stato di sincronizzazione
 *
 * Stati visuali:
 * - Offline: rosso con icona WifiOff
 * - Syncing: blu con spinner
 * - Pending: giallo/arancio con conteggio
 * - Online: verde (opzionale, nascosto di default)
 */
import React from 'react';
import { WifiOff, Wifi, RefreshCw, Clock, Check } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import { cn } from '@/shared/lib/utils';
import { useMutationQueue } from '@/contexts/MutationQueueContext';
import { CacheStatsPopover } from '@/core/offline/components/CacheStatsPopover';

interface SyncStatusBadgeProps {
  showWhenOnline?: boolean;
  showCount?: boolean;
  compact?: boolean;
  className?: string;
}

export const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({
  showWhenOnline = false,
  showCount = true,
  compact = false,
  className = '',
}) => {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    triggerSync,
  } = useMutationQueue();

  // Determina lo stato attuale
  const getStatus = () => {
    if (!isOnline) return 'offline';
    if (isSyncing) return 'syncing';
    if (pendingCount > 0) return 'pending';
    return 'online';
  };

  const status = getStatus();

  // Non mostrare se online e non configurato per farlo
  if (status === 'online' && !showWhenOnline) {
    return null;
  }

  // Configurazione visuale per ogni stato
  const statusConfig = {
    offline: {
      icon: WifiOff,
      label: 'Offline',
      description: 'Modalità offline attiva. Le operazioni verranno sincronizzate quando tornerai online.',
      badgeVariant: 'destructive' as const,
      iconClassName: 'text-destructive-foreground',
      bgClassName: 'bg-destructive hover:bg-destructive/90',
    },
    syncing: {
      icon: RefreshCw,
      label: 'Sincronizzando...',
      description: 'Sincronizzazione in corso...',
      badgeVariant: 'default' as const,
      iconClassName: 'text-primary-foreground animate-spin',
      bgClassName: 'bg-blue-500 hover:bg-blue-600',
    },
    pending: {
      icon: Clock,
      label: `${pendingCount} in coda`,
      description: `${pendingCount} operazioni in attesa di sincronizzazione. Tocca per sincronizzare ora.`,
      badgeVariant: 'secondary' as const,
      iconClassName: 'text-orange-600 dark:text-orange-400',
      bgClassName: 'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/50 dark:hover:bg-orange-900/70',
    },
    online: {
      icon: lastSyncTime ? Check : Wifi,
      label: 'Online',
      description: lastSyncTime
        ? `Ultimo sync: ${lastSyncTime.toLocaleTimeString('it-IT')}`
        : 'Connesso e sincronizzato',
      badgeVariant: 'outline' as const,
      iconClassName: 'text-green-600 dark:text-green-400',
      bgClassName: 'bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  // Gestione click per sync manuale
  const handleClick = () => {
    if (status === 'pending' && isOnline) {
      triggerSync();
    }
  };

  // Versione compatta (solo icona) - con popover per statistiche cache
  if (compact) {
    return (
      <CacheStatsPopover>
        <Button
          variant="ghost"
          size="sm"
          disabled={status === 'syncing'}
          className={cn(
            'relative h-8 w-8 p-0 rounded-full',
            className
          )}
        >
          <Icon className={cn('h-4 w-4', config.iconClassName)} />
          {status === 'pending' && showCount && pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </Button>
      </CacheStatsPopover>
    );
  }

  // Versione estesa (badge con testo)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant={config.badgeVariant}
            onClick={handleClick}
            className={cn(
              'flex items-center gap-1.5 px-2 py-1 cursor-default transition-colors',
              status === 'pending' && isOnline && 'cursor-pointer',
              config.bgClassName,
              className
            )}
          >
            <Icon className={cn('h-3.5 w-3.5', config.iconClassName)} />
            <span className="text-xs font-medium">
              {status === 'pending' && showCount ? `${pendingCount} in coda` : config.label}
            </span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <p className="text-xs">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SyncStatusBadge;
